import { type TImageElement } from '@alpod-ai-writer/plate-plugins/media';
import { type AttachmentNumberingConfig, CITATION_LIST_FIELD, type CitationGroup, type DocumentConfig, type DocumentData, type DocumentExportRequestData, DocumentSectionType, type DocumentTemplate, SHORTCUT_LIST_FIELD, type SlateContents } from '@alpod-ai-writer/shared';
import { yTextToSlateElement } from '@slate-yjs/core';
import { type Descendant, Element } from 'slate';
import * as Y from 'yjs';

import { getOtherAttachmentTitles } from '../components/otherAttachmentsEditor.component';

import { EditorImageMaxWidth } from '../constants';

export function walkSlateElements(node: Descendant, callback: (node: Element) => void) {
    if (Element.isElement(node)) {
        callback(node);

        if (node.children) {
            node.children.forEach((child) => {
                walkSlateElements(child, callback);
            });
        }
    }
}

export function getDocumentData(template: DocumentTemplate, document: Y.Doc): DocumentData {
    const fields: Record<string, unknown> = {};
    const contents: Record<string, SlateContents> = {};

    const optOutMap = document.getMap<boolean>('sectionOptOut');

    template.config.sections.forEach((section) => {
        switch (section.type) {
            case DocumentSectionType.Attachments: {
                const arr = document.getArray<Y.Map<string>>('att_items_' + section.contentId);
                const itemIds = arr.toArray().map((m) => m.get('id') ?? '');

                if (itemIds.length === 0) {
                    // No attachments — send empty block so generator clears the placeholder and its heading.
                    contents[section.contentId] = { children: [] } as unknown as SlateContents;
                    break;
                }

                const blocks: unknown[] = [];
                for (const id of itemIds) {
                    const title = document.getText('att_title_' + id).toString();
                    blocks.push({ type: 'h-attachment', children: [ { text: title } ] });

                    const bodyNode = document.get('att_body_' + id, Y.XmlText);
                    if (bodyNode instanceof Y.XmlText) {
                        const slateContent = yTextToSlateElement(bodyNode);
                        walkSlateElements(slateContent, (element) => {
                            if (Element.isElementType<TImageElement & { width?: number; }>(element, 'img')) {
                                if (element.width) {
                                    element.width = Math.round(element.width / EditorImageMaxWidth * 100);
                                }
                            }
                            if ((element as any).type === 'table' && Array.isArray((element as any).colSizes)) {
                                (element as any).colSizes = (element as any).colSizes.map((s: number) => Math.round(s));
                            }
                            if ((element as any).listStyleType === '') {
                                (element as any).listStyleType = undefined;
                            }
                            if ((element as any).type === 'td' || (element as any).type === 'th') {
                                const attrs = (element as any).attributes;
                                if (attrs?.colspan) (element as any).colSpan = parseInt(attrs.colspan);
                                if (attrs?.rowspan) (element as any).rowSpan = parseInt(attrs.rowspan);
                            }
                        });
                        blocks.push(...slateContent.children);
                    }
                }

                contents[section.contentId] = { children: blocks } as SlateContents;
                break;
            }
            case DocumentSectionType.OtherAttachments: {
                const isOptedOut = optOutMap.get(section.id) === true;
                if (isOptedOut) {
                    contents[section.contentId] = { children: [] } as unknown as SlateContents;
                    break;
                }
                const titles = getOtherAttachmentTitles(document, section.contentId);
                if (titles.length === 0) {
                    contents[section.contentId] = { children: [] } as unknown as SlateContents;
                    break;
                }
                contents[section.contentId] = {
                    children: titles.map((title) => ({
                        type: 'p',
                        children: [ { text: title } ],
                    })),
                } as unknown as SlateContents;
                break;
            }
            case DocumentSectionType.Content: {
                section.fields.map((field) => {
                    const value = document.getText(field.id).toJSON();
                    fields[field.id] = value;
                });

                if (section.content && section.content.id) {
                    if (optOutMap.get(section.id) === true) {
                        contents[section.content.id] = { children: [] };
                        break;
                    }

                    const docContent = document.get(section.content.id, Y.XmlText);

                    if (docContent instanceof Y.XmlText) {
                        const slateContent = yTextToSlateElement(docContent);

                       walkSlateElements(slateContent, (element) => {
    if (Element.isElementType<TImageElement & { width?: number; }>(element, 'img')) {
        if (element.width) {
            element.width = Math.round(element.width / EditorImageMaxWidth * 100);
        }
    }
    // Fix colSizes float -> int
    if ((element as any).type === 'table' && Array.isArray((element as any).colSizes)) {
        (element as any).colSizes = (element as any).colSizes.map((s: number) => Math.round(s));
    }
    // Fix empty listStyleType
    if ((element as any).listStyleType === '') {
        (element as any).listStyleType = undefined;
    }
    // Fix table cell colSpan/rowSpan from attributes
    if ((element as any).type === 'td' || (element as any).type === 'th') {
        const attrs = (element as any).attributes;
        if (attrs?.colspan) {
            (element as any).colSpan = parseInt(attrs.colspan);
        }
        if (attrs?.rowspan) {
            (element as any).rowSpan = parseInt(attrs.rowspan);
        }
    }
});


                        contents[section.content.id] = slateContent;
                    }
                }

                break;
            }
        }
    });

    return {
        templatePath: template.docxPath,
        fields,
        contents,
        citations: document.getMap(CITATION_LIST_FIELD).toJSON(),
        shortcuts: document.getMap(SHORTCUT_LIST_FIELD).toJSON(),
    };
}

export function getDocumentExportRequestData(config: DocumentConfig, data: DocumentData, documentType?: string, documentName?: string): DocumentExportRequestData {
    const typeVariables = documentType ? (config.types[documentType]?.variables ?? {}) : {};

    const citationListSection = config.sections.find((s) => s.type === DocumentSectionType.CitationList);
    const citationGroups: CitationGroup[] | undefined =
        citationListSection?.type === DocumentSectionType.CitationList && citationListSection.citationGroups?.length
            ? citationListSection.citationGroups
            : undefined;

    const attachmentsSection = config.sections.find((s) => s.type === DocumentSectionType.Attachments);
    const attachmentNumberingConfig: AttachmentNumberingConfig | undefined =
        attachmentsSection?.type === DocumentSectionType.Attachments &&
        attachmentsSection.attachmentNumberingMode === 'custom' &&
        attachmentsSection.attachmentLabel
            ? {
                label: attachmentsSection.attachmentLabel,
                numberingType: attachmentsSection.attachmentNumberingType ?? 'arabic',
                numberPosition: attachmentsSection.attachmentNumberPosition ?? 'after',
                trailingDot: attachmentsSection.attachmentTrailingDot ?? false,
            }
            : undefined;

    const shortcutListSection = config.sections.find((s) => s.type === DocumentSectionType.ShortcutList);
    const shortcutListSeparator =
        shortcutListSection?.type === DocumentSectionType.ShortcutList
            ? (shortcutListSection.shortcutListSeparator ?? 'dash')
            : 'dash';

    const attachmentListSection = config.sections.find((s) => s.type === DocumentSectionType.AttachmentList);
    const attachmentListTocShowPageNumbers =
        attachmentListSection?.type === DocumentSectionType.AttachmentList
            ? (attachmentListSection.tocShowPageNumbers ?? true)
            : true;

    const figureMinCounts: Record<string, number> = {};
    for (const section of config.sections) {
        if (section.type !== DocumentSectionType.FigureList || !section.figureMinCount) continue;
        const min = section.figureMinCount;
        const kind = section.figureListKind ?? 'all';
        if (kind === 'all' || kind === 'images') figureMinCounts['Figure'] = Math.max(figureMinCounts['Figure'] ?? 1, min);
        if (kind === 'all' || kind === 'tables') figureMinCounts['Table'] = Math.max(figureMinCounts['Table'] ?? 1, min);
        if (kind === 'all' || kind === 'charts') figureMinCounts['Chart'] = Math.max(figureMinCounts['Chart'] ?? 1, min);
        if (kind === 'all' || kind === 'maps') figureMinCounts['Map'] = Math.max(figureMinCounts['Map'] ?? 1, min);
        if (kind === 'all' || kind === 'formulas') figureMinCounts['Formula'] = Math.max(figureMinCounts['Formula'] ?? 1, min);
        if (kind === 'all' || kind === 'other') figureMinCounts['Other'] = Math.max(figureMinCounts['Other'] ?? 1, min);
    }

    const noPageBreakSections = config.sections
        .filter((s) => s.noPageBreak && s.type === DocumentSectionType.Content && s.content?.id)
        .map((s) => (s as typeof s & { content: { id: string } }).content.id);

    return {
        ...data,
        fields: { ...typeVariables, ...data.fields },
        captionsPosition: config.captionsPosition,
        captionsPositionForImages: config.captionsPositionForImages,
        captionsPositionForTables: config.captionsPositionForTables,
        captionsPositionForCharts: config.captionsPositionForCharts,
        citationStyle: config.citationStyle,
        figurePrefixes: config.figurePrefixes,
        citationPageNumberPrefix: config.citationPageNumberPrefix,
        language: config.language,
        citationGroups,
        attachmentNumberingConfig,
        figureMinCounts: Object.keys(figureMinCounts).length > 0 ? figureMinCounts : undefined,
        attachmentListTocShowPageNumbers,
        shortcutListSeparator,
        captionChapterNumbers: config.captionChapterNumbers ?? false,
        noPageBreakSections: noPageBreakSections.length > 0 ? noPageBreakSections : undefined,
    };
}
